<script>
  import { onMount } from 'svelte'
  import { createEventDispatcher } from 'svelte'
  import { createUser, getUsers } from '../services/api.js'
  import { currentUser } from '../stores/auth.js'

  const dispatch = createEventDispatcher()

  let email = ''
  let password = ''
  let name = ''
  let isSubmitting = false
  let errorMessage = ''
  let successMessage = ''
  let users = []
  let isLoadingUsers = false
  let showCreateForm = false

  onMount(async () => {
    await loadUsers()
  })

  async function loadUsers() {
    isLoadingUsers = true
    try {
      const result = await getUsers()
      if (result.success) {
        users = result.data
      } else {
        // Supabase Auth doesn't support listing users via API
        console.info('User listing not available with Supabase Auth')
        users = []
      }
    } catch (error) {
      console.error('Failed to load users:', error)
      users = []
    } finally {
      isLoadingUsers = false
    }
  }

  async function handleSubmit() {
    // Reset messages
    errorMessage = ''
    successMessage = ''

    // Validate
    if (!email || !password) {
      errorMessage = '請填寫 Email 與密碼'
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      errorMessage = 'Email 格式不正確'
      return
    }

    // Validate password length
    if (password.length < 6) {
      errorMessage = '密碼至少需要 6 個字元'
      return
    }

    isSubmitting = true

    try {
      const result = await createUser(email, password, name)

      if (result.success) {
        successMessage = `使用者 ${result.data.email} 建立成功！`
        // Reset form
        email = ''
        password = ''
        name = ''
        // Reload users list
        await loadUsers()
        // Hide create form after 2 seconds
        setTimeout(() => {
          showCreateForm = false
          successMessage = ''
        }, 2000)
      } else {
        errorMessage = result.error || '建立失敗'
      }
    } catch (error) {
      console.error('Create user error:', error)
      errorMessage = error.message || '建立失敗'
    } finally {
      isSubmitting = false
    }
  }

  function handleClose() {
    dispatch('close')
  }
</script>

<div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
  <div class="relative top-20 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-bold text-gray-900">使用者管理</h2>
      <button
        on:click={handleClose}
        class="text-gray-400 hover:text-gray-500"
        aria-label="關閉"
      >
        ✕
      </button>
    </div>

    <!-- Current user info -->
    <div class="mb-4 p-3 bg-blue-50 rounded-md">
      <p class="text-sm text-gray-600">
        目前登入為: <span class="font-medium">{$currentUser?.name || $currentUser?.email}</span>
      </p>
    </div>

    <!-- User List -->
    {#if !showCreateForm}
      <div class="mb-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-lg font-medium text-gray-900">使用者列表</h3>
          <button
            on:click={() => showCreateForm = true}
            class="bg-primary-600 text-white px-3 py-1.5 rounded-md hover:bg-primary-700 transition-colors text-sm"
          >
            + 建立新使用者
          </button>
        </div>

        {#if isLoadingUsers}
          <div class="text-center py-8">
            <div class="spinner mx-auto mb-2"></div>
            <p class="text-sm text-gray-500">載入中...</p>
          </div>
        {:else if users.length === 0}
          <div class="text-center py-8">
            <div class="mb-4">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 class="text-sm font-medium text-gray-900 mb-1">無法顯示使用者列表</h3>
            <p class="text-sm text-gray-500 mb-4">基於安全性考量，Supabase Auth 不支援透過 API 列出所有使用者</p>
            <div class="text-xs text-gray-400 space-y-1">
              <p>• 您仍可以建立新使用者</p>
              <p>• 使用者管理請至 <a href="https://supabase.com/dashboard" target="_blank" class="text-primary-600 hover:underline">Supabase Dashboard</a></p>
            </div>
          </div>
        {:else}
          <div class="border border-gray-200 rounded-md overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名稱</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建立時間</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                {#each users as user}
                  <tr class:bg-blue-50={user.id === $currentUser?.id}>
                    <td class="px-4 py-3 text-sm text-gray-900">
                      {user.name}
                      {#if user.id === $currentUser?.id}
                        <span class="ml-2 text-xs text-blue-600">(你)</span>
                      {/if}
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                    <td class="px-4 py-3 text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString('zh-TW')}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    {:else}
      <!-- Create User Form -->
      <div class="mb-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-lg font-medium text-gray-900">建立新使用者</h3>
          <button
            on:click={() => {
              showCreateForm = false
              errorMessage = ''
              successMessage = ''
            }}
            class="text-sm text-gray-600 hover:text-gray-800"
          >
            ← 返回列表
          </button>
        </div>
      </div>

      <!-- Form -->
    <form on:submit|preventDefault={handleSubmit} class="space-y-4">
      <!-- Email -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Email <span class="text-red-500">*</span>
        </label>
        <input
          type="email"
          bind:value={email}
          disabled={isSubmitting}
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="user@example.com"
          required
        />
      </div>

      <!-- Password -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          密碼 <span class="text-red-500">*</span>
        </label>
        <input
          type="password"
          bind:value={password}
          disabled={isSubmitting}
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="至少 6 個字元"
          required
        />
        <p class="text-xs text-gray-500 mt-1">密碼至少需要 6 個字元</p>
      </div>

      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          名稱 (選填)
        </label>
        <input
          type="text"
          bind:value={name}
          disabled={isSubmitting}
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="若不填寫,將使用 email 前綴作為名稱"
        />
      </div>

      <!-- Error message -->
      {#if errorMessage}
        <div class="p-3 bg-red-50 border border-red-200 rounded-md">
          <p class="text-sm text-red-600">{errorMessage}</p>
        </div>
      {/if}

      <!-- Success message -->
      {#if successMessage}
        <div class="p-3 bg-green-50 border border-green-200 rounded-md">
          <p class="text-sm text-green-600">{successMessage}</p>
        </div>
      {/if}

      <!-- Buttons -->
      <div class="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          class="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? '建立中...' : '建立使用者'}
        </button>
        <button
          type="button"
          on:click={() => {
            showCreateForm = false
            errorMessage = ''
            successMessage = ''
          }}
          disabled={isSubmitting}
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
        >
          取消
        </button>
      </div>
    </form>

    <!-- Instructions -->
    <div class="mt-4 p-3 bg-gray-50 rounded-md">
      <p class="text-xs text-gray-600">
        密碼會經過 SHA-256 雜湊後儲存。建立成功後，新使用者就可以用這組帳密登入。
      </p>
    </div>
    {/if}
  </div>
</div>
